import { type FC, type ReactNode } from "react";

import { clsx } from "clsx";

import { faXmark } from "@fortawesome/pro-duotone-svg-icons/faXmark";

import { IconButton } from "@styled/icon_button";

// The card's colour carries as much meaning as its copy — a green rule and a
// green header read as "done" before the title is read. `attention` is for a
// case that finished with records missing: not success, not a hard failure.
const TONE = {
  success: {
    frame: "border-l-emerald-500",
    header: "border-emerald-200 bg-emerald-50",
    icon: "bg-emerald-500",
  },
  attention: {
    frame: "border-l-amber-500",
    header: "border-amber-200 bg-amber-50",
    icon: "bg-amber-500",
  },
} as const;

export const CompletedRecordsCard: FC<{
  id?: string;
  icon: ReactNode;
  title: ReactNode;
  subtitle?: ReactNode;
  badge?: ReactNode;
  actions?: ReactNode;
  statsBody: ReactNode;
  fileList: ReactNode;
  tone?: keyof typeof TONE;
  // When set, renders a ✕ at the header's far right that collapses the card.
  // Wired only when the card is the embedded, expandable processing card —
  // standalone renders leave it undefined, so no close appears.
  onClose?: () => void;
}> = ({
  id,
  icon,
  title,
  subtitle,
  badge,
  actions,
  statsBody,
  fileList,
  tone = "success",
  onClose,
}) => (
  <div
    id={id}
    className={clsx(
      "overflow-hidden rounded-[12px] border border-l-4 border-slate-200 bg-white",
      TONE[tone].frame,
    )}
  >
    <div
      className={clsx(
        "flex items-center gap-3 border-b px-[18px] py-3",
        TONE[tone].header,
      )}
    >
      <div
        className={clsx(
          "flex size-6 shrink-0 items-center justify-center rounded-full text-[13px] font-bold text-white",
          TONE[tone].icon,
        )}
      >
        {icon}
      </div>
      <div className="flex-1">
        <div className="text-sm font-semibold text-slate-900">{title}</div>
        {subtitle && (
          <div className="mt-0.5 text-xs text-slate-500">{subtitle}</div>
        )}
      </div>
      {badge}
      {actions && (
        <div className="flex shrink-0 items-center gap-2">{actions}</div>
      )}
      {onClose && (
        <IconButton
          icon={faXmark}
          onClick={onClose}
          aria-label="Hide processing details"
          title="Hide processing details"
        />
      )}
    </div>
    <div className="px-5 py-3">{statsBody}</div>
    <hr className="border-slate-100" />
    <div className="px-5 py-3">{fileList}</div>
  </div>
);
